"use client";

import type { ReactNode } from "react";
import { useI18n } from "@/lib/i18n";
import { freshnessPresentation } from "@/lib/freshness";
import type { Tone } from "@/lib/types";
import { useOnlineStatus } from "@/lib/use-online-status";

const toneClass: Record<Tone, string> = {
  positive: "border-[var(--hc-positive)]/40 bg-[var(--hc-positive-soft)] text-[var(--hc-positive)]",
  warning: "border-[var(--hc-warning)]/40 bg-[var(--hc-warning-soft)] text-[var(--hc-warning)]",
  neutral: "border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] text-[var(--hc-muted)]",
  danger: "border-[var(--hc-negative)]/40 bg-[var(--hc-negative-soft)] text-[var(--hc-negative)]"
};

const toneDot: Record<Tone, string> = {
  positive: "bg-emerald-400",
  warning: "bg-amber-400",
  neutral: "bg-slate-400",
  danger: "bg-rose-400"
};

const CARD_LABEL_KEYS: Record<string, string> = {
  "Global CB Signal": "card.cb",
  "Manufacturing Cycle": "card.cycle",
  "ETF Flow Pulse": "card.etf",
  "BTC Vol Surface": "card.vol",
  "Data Freshness": "card.fresh"
};

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`min-w-0 rounded-lg border border-[var(--hc-border)] bg-[var(--hc-surface-1)] p-4 md:p-5 ${className}`}
    >
      {children}
    </section>
  );
}

export function PanelHeader({ title, meta, children }: { title: string; meta?: string; children?: ReactNode }) {
  return (
    <div className="mb-4 flex min-h-9 items-start justify-between gap-3">
      <h2 className="text-sm font-semibold tracking-[-0.01em] text-[var(--hc-text)]">{title}</h2>
      {children ?? (meta ? <span className="rounded border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-2 py-1 text-[11px] text-[var(--hc-muted)]">{meta}</span> : null)}
    </div>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone }) {
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 rounded border px-2 py-1 text-[11px] font-semibold tracking-[0.02em] ${toneClass[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${toneDot[tone]}`} />
      {children}
    </span>
  );
}

export function PeriodTabs({
  periods,
  value,
  onChange
}: {
  periods: string[];
  value: string;
  onChange: (period: string) => void;
}) {
  return (
    <div className="grid w-full max-w-full grid-cols-4 gap-0.5 rounded-md border border-[var(--hc-border)] bg-[var(--hc-bg)] p-0.5 sm:w-auto" role="group" aria-label="Chart period">
      {periods.map((period) => (
        <button
          key={period}
          type="button"
          onClick={() => onChange(period)}
          aria-pressed={period === value}
          className={`min-h-11 min-w-11 touch-manipulation rounded px-2 py-1 text-[11px] font-semibold transition-colors sm:min-h-9 sm:min-w-10 sm:px-2.5 ${
            period === value ? "bg-[var(--hc-blue-soft)] text-[var(--hc-text)]" : "text-[var(--hc-muted)] hover:bg-[var(--hc-surface-2)] hover:text-[var(--hc-text)]"
          }`}
        >
          {period}
        </button>
      ))}
    </div>
  );
}

export function FreshnessTag({ status, source, collectedAt, online = true }: { status?: string; source?: string; collectedAt?: string | null; online?: boolean }) {
  const { t } = useI18n();
  const detectedOnline = useOnlineStatus();
  if (!status) return null;
  const presentation = freshnessPresentation(status, online && detectedOnline, collectedAt);
  const liveLabel = presentation.label === "LIVE" ? t("freshness.live") : presentation.label;
  const sourceLabel = source ? ` · ${source}` : "";
  const offlineDetail = presentation.label === "OFFLINE" ? ` · ${presentation.detail}` : "";
  return <span title={`${source ?? "Source"} · ${presentation.detail}`}><Badge tone={presentation.tone}>{liveLabel}{sourceLabel}{offlineDetail}</Badge></span>;
}

export function StatCard({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: Tone }) {
  const { t } = useI18n();
  const displayLabel = CARD_LABEL_KEYS[label] ? t(CARD_LABEL_KEYS[label]) : label;
  return (
    <Panel>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 break-words text-xs uppercase tracking-[0.12em] text-[var(--hc-muted)]">{displayLabel}</p>
        <Badge tone={tone}>{tone}</Badge>
      </div>
      <p className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-[var(--hc-text)]">{value}</p>
      <p className="mt-2 text-sm leading-5 text-[var(--hc-muted)]">{detail}</p>
    </Panel>
  );
}
